import { roundWarehouseQuantity } from '../../utils/warehouseQuantity';
import { roundMoney } from '../../utils/money';
import type { WZIssueBatch, WZIssueLine } from './wzIssueApi';

/**
 * Pure rules of the „Wystaw WZ” window (decision 2026-10-04, points 1–2).
 *
 * The backend (`wzBatchPickService.previewOrderIssue`) sends, per order line,
 * the batches it can come from and the FIFO slots (this order's own pins
 * first, then free stock, oldest batch first). Walking the slots is the same
 * plan the release runs, so this module never re-derives FIFO; it only takes
 * from the slots, validates the operator's numbers and says whether a manual
 * pick differs from FIFO (then a reason is required).
 */

export type WZIssueMode = 'fifo' | 'manual';

export interface WZIssuePart {
  batch: WZIssueBatch;
  quantity: number;
}

export const WZ_ISSUE_REASON_MIN_LENGTH = 3;
const EPSILON = 0.0005;

const round = (value: number) => roundWarehouseQuantity(Math.max(0, Number(value) || 0));

export function lineKey(line: Pick<WZIssueLine, 'sourceOrderProductId' | 'productVariantId'>, index: number): string {
  return line.sourceOrderProductId || `${line.productVariantId || 'line'}-${index}`;
}

/**
 * FIFO take of `quantity` from the line's slots, aggregated per batch.
 * `used` carries what earlier lines of the same WZ already took per batch:
 * several lines of one product share the free batches (owner 2026-10-12,
 * film 03/12/2025 — five lines must not all show the oldest PZ).
 */
export function takeFifo(
  line: WZIssueLine,
  quantity: number,
  used?: Map<string, number>,
): { parts: WZIssuePart[]; shortfall: number } {
  let left = round(quantity);
  const taken = new Map<string, number>();
  const room = (batchId: string) => {
    if (!used) return Number.POSITIVE_INFINITY;
    const batch = line.batches.find((row) => row.inventoryBatchId === batchId);
    if (!batch) return Number.POSITIVE_INFINITY;
    return round(batch.available - (used.get(batchId) || 0) - (taken.get(batchId) || 0));
  };
  for (const slot of line.fifoSlots) {
    if (left <= EPSILON) break;
    const take = round(Math.min(slot.quantity, left, room(slot.inventoryBatchId)));
    if (!(take > 0)) continue;
    taken.set(slot.inventoryBatchId, round((taken.get(slot.inventoryBatchId) || 0) + take));
    left = round(left - take);
  }
  const parts: WZIssuePart[] = [];
  taken.forEach((value, batchId) => {
    const batch = line.batches.find((row) => row.inventoryBatchId === batchId);
    if (batch) parts.push({ batch, quantity: value });
    if (used) used.set(batchId, round((used.get(batchId) || 0) + value));
  });
  return { parts, shortfall: left };
}

/** FIFO plan of the whole WZ: lines in order, each taking what the earlier ones left. */
export function planFifo(entries: Array<{ key: string; line: WZIssueLine; quantity: number }>): Map<string, { parts: WZIssuePart[]; shortfall: number }> {
  const used = new Map<string, number>();
  return new Map(entries.map((entry) => [entry.key, takeFifo(entry.line, entry.quantity, used)]));
}

/** Default „W tej WZ”: min(left to issue, on stock). */
export function defaultQuantity(line: WZIssueLine): number {
  return round(Math.min(line.remaining, line.onStock));
}

/**
 * „W tej WZ” may exceed the stock (owner 2026-10-27, szkic WZ przed PZ): the
 * missing part makes the WZ a draft ({@link hasShortfall}), never more than is
 * left to issue on the order.
 */
export function isFifoQuantityValid(line: WZIssueLine, quantity: number | null | undefined): boolean {
  if (quantity === null || quantity === undefined) return true;
  const value = Number(quantity);
  return Number.isFinite(value) && value >= 0
    && value <= line.remaining + EPSILON;
}

/**
 * True when the FIFO plan lacks goods on stock: the window then only saves
 * the WZ as a draft (number + PDF, nothing leaves the warehouse); the goods go
 * out with „Rozlicz wydanie” on the WZ after the PZ.
 */
export function hasShortfall(plan: Array<{ shortfall: number }>): boolean {
  return plan.some((entry) => entry.shortfall > EPSILON);
}

/** Manual picks of one line as parts (zero picks dropped). */
export function manualParts(line: WZIssueLine, picks: Record<string, number | null | undefined> = {}): WZIssuePart[] {
  return line.batches
    .map((batch) => ({ batch, quantity: round(Number(picks[batch.inventoryBatchId]) || 0) }))
    .filter((part) => part.quantity > 0);
}

export function isPickValid(batch: WZIssueBatch, value: number | null | undefined): boolean {
  if (value === null || value === undefined) return true;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 && numeric <= batch.available + EPSILON;
}

export function sumParts(parts: WZIssuePart[]): number {
  return round(parts.reduce((sum, part) => sum + part.quantity, 0));
}

export function isManualLineValid(line: WZIssueLine, picks: Record<string, number | null | undefined> = {}): boolean {
  return line.batches.every((batch) => isPickValid(batch, picks[batch.inventoryBatchId]))
    && sumParts(manualParts(line, picks)) <= line.remaining + EPSILON;
}

/** True when the manual picks of a line differ from what FIFO takes for the same total. */
export function differsFromFifo(
  line: WZIssueLine,
  picks: Record<string, number | null | undefined> = {},
  fifoParts?: WZIssuePart[],
): boolean {
  const parts = manualParts(line, picks);
  const fifo = fifoParts ?? takeFifo(line, sumParts(parts)).parts;
  if (parts.length !== fifo.length) return true;
  return parts.some((part) => {
    const match = fifo.find((row) => row.batch.inventoryBatchId === part.batch.inventoryBatchId);
    return !match || Math.abs(match.quantity - part.quantity) > EPSILON;
  });
}

/** Picks seeded from the FIFO plan (switching to „Wybiorę partie”). */
export function picksFromFifo(line: WZIssueLine, quantity: number, used?: Map<string, number>): Record<string, number> {
  const picks: Record<string, number> = {};
  takeFifo(line, quantity, used).parts.forEach((part) => { picks[part.batch.inventoryBatchId] = part.quantity; });
  return picks;
}

/**
 * Cost of one take, priced exactly like the release
 * (inventoryService._consumeFromBatch): the batch's remaining cost × the
 * share of its remaining quantity taken, rounded to grosze; the whole rest of
 * the batch costs its whole remaining cost. Without a stored batch total the
 * release uses the unit cost, so does this.
 */
const SET_UNITS = ['pcs', 'm2', 'm3'] as const;

/**
 * Share of the batch a take consumes, compared the way the release does
 * (commercialQuantity.takeQuantitySet): in the FIRST unit both the batch set
 * and the line set carry (pcs, then m², then m³) — not in the line unit, so a
 * rounded m² never shifts the cost by a grosz. null without the sets.
 */
export function batchShare(part: WZIssuePart, line?: Pick<WZIssueLine, 'remaining' | 'quantitySet'>): number | null {
  const batchSet = part.batch.remainingSet;
  const lineSet = line?.quantitySet;
  if (!batchSet || !lineSet || !(line && line.remaining > 0)) return null;
  const scale = part.quantity / line.remaining;
  for (const unit of SET_UNITS) {
    const available = batchSet[unit];
    const perLine = lineSet[unit];
    if (available === null || available === undefined || perLine === null || perLine === undefined) continue;
    if (!(available > 0)) return 0;
    return Math.min(available, perLine * scale) / available;
  }
  return null;
}

export function partCost(part: WZIssuePart, line?: Pick<WZIssueLine, 'remaining' | 'quantitySet'>): number | null {
  const { batch, quantity } = part;
  const remainingCost = batch.remainingCostPLN;
  if (remainingCost !== null && remainingCost !== undefined && batch.remainingQuantity > 0) {
    const share = batchShare(part, line) ?? quantity / batch.remainingQuantity;
    if (share >= 1 - 1e-9 || quantity >= batch.remainingQuantity - EPSILON) return roundMoney(remainingCost);
    return Math.min(roundMoney(remainingCost * share), roundMoney(remainingCost));
  }
  if (batch.unitCostPLN === null || batch.unitCostPLN === undefined) return null;
  return roundMoney(quantity * batch.unitCostPLN);
}

/** Cost of the parts in PLN (= the WZ costOfGoodsSold), or null when a batch cost is not visible / unknown. */
export function partsCost(parts: WZIssuePart[], lineOf?: (part: WZIssuePart) => Pick<WZIssueLine, 'remaining' | 'quantitySet'> | undefined): number | null {
  let total = 0;
  for (const part of parts) {
    const cost = partCost(part, lineOf?.(part));
    if (cost === null) return null;
    total += cost;
  }
  return roundMoney(total);
}

/**
 * Goods of this WZ without an approved purchase invoice: a batch taken whose
 * receipt has no approved invoice, or quantity not on stock at all. The
 * release then asks for a reason (WP12 allow-with-confirmation); the window
 * asks for it up front. Unknown provenance (older payload) never warns.
 */
export function partsWithoutPurchaseInvoice(plan: Array<{ line: WZIssueLine; quantity: number; parts: WZIssuePart[] }>): string[] {
  const names = new Set<string>();
  plan.forEach(({ line, quantity, parts }) => {
    if (!(quantity > 0)) return;
    const uninvoiced = parts.some((part) => part.batch.purchaseInvoiceApproved === false);
    const shortfall = quantity - sumParts(parts) > EPSILON;
    if (uninvoiced || shortfall) names.add(line.productName);
  });
  return Array.from(names);
}
